import { css } from '@emotion/react';
import { styled } from '@mui/material/styles';
import { useAtomValue } from 'jotai';
import { Group, Panel } from 'react-resizable-panels';
import { Outlet, useLocation } from 'react-router';
import { GenericFallbacksWrapper } from './components/Fallback/GenericFallbacksWrapper';
import { ChatBotSection } from './components/ChatBotSection/ChatBotSection';
import { Header } from './components/Layout/Header/Header';
import { Header2 } from './components/Layout/Header/Header2';
import { Navbar } from './components/Layout/Navbar/Navbar';
import { AdminPanelNavigation } from './components/Layout/Navbar/Navigation/AdminPanel/AdminPanelNavigation';
import { CPNavigation } from './components/Layout/Navbar/Navigation/CompanyProfiles/CPNavigation';
import { DealflowNavigation } from './components/Layout/Navbar/Navigation/Dealflow/DealflowNavigation';
import { EntityManagementNavigation } from './components/Layout/Navbar/Navigation/EntityManagement/EntityManagementNavigation';
import { FinanceNavigation } from './components/Layout/Navbar/Navigation/Finance/FinanceNavigation';
import { KPINavigation } from './components/Layout/Navbar/Navigation/KPI/KPINavigation';
import { PortfolioReportingNavigation } from './components/Layout/Navbar/Navigation/PortfolioReporting/PortfolioReportingNavigation';
import { SourcingNavigation } from './components/Layout/Navbar/Navigation/Sourcing/SourcingNavigation';
import { ROUTES } from './constants/RouteNameMapping';
import { ContainerName, ZINDEX } from './constants/styles';
import { useFdSearchParams } from './hooks/useFdSearchParams';
import { EntityManagementHeaderActions } from './pages/EntityManagement/components/EntityManagementHeaderActions';
import { AddDealFormModal } from './pages/DealFlow2/Forms/AddDealFormModal';
import { DealActionModals } from './pages/DealFlow2/modals/DealActionModals';
import { isPresentModeState } from './pages/DealFlow2/state/DealboardUIStateJ';
import { PermissionKey, PersonaKey } from './services/PermissionAndRolesKeys';
import { PermissionService } from './services/PermissionService';

const StyledWrapper = css`
  min-height: 0;
  display: grid;
  height: 100%;
  width: 100%;
  grid-template-columns: min-content 1fr min-content; /* additional side nav | content | chatbot drawer */
  &.present-mode {
    grid-template-columns: 1fr;
  }
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  flex-grow: 1;
`;

const HeaderContainer = styled('div')`
  container-type: inline-size;
  container-name: ${ContainerName.headerContainer};
  position: relative;
  z-index: ${ZINDEX.APP_HEADER};
`;

const ChildrenContainer = styled('div')`
  display: flex;
  overflow: auto;
  min-height: 0;
  flex: 1;
  background: ${({ theme }) => theme.gradients.primary};
  background-size: cover;
`;

const MenuWrapper = styled('div')<{ isHidden: boolean }>`
  display: grid;
  grid-template-columns: 72px min-content;
  ${({ isHidden: hidden }) => hidden && 'display: none;'}
`;

export function NavWrapper() {
  const { pathname } = useLocation();
  const innerComponent = getInnerNavComponent(pathname);
  const innerHeaderMiddleSlot = getHeaderChildrenByLocation(pathname);
  const presentModeOn = useAtomValue(isPresentModeState);
  const { noCpNav } = useFdSearchParams();
  const isAuditor = PermissionService.get().isAuditor();
  const showHeader =
    !pathname.includes(ROUTES.BULK_UPLOAD_REPORT) &&
    !PermissionService.get().hasPersona(PersonaKey.managedServices) &&
    !isAuditor;

  return (
    <>
      <div css={StyledWrapper} className={presentModeOn ? 'present-mode' : ''}>
        <MenuWrapper isHidden={presentModeOn}>
          <Navbar />
          <GenericFallbacksWrapper suspenseFallback={<></>}>{innerComponent}</GenericFallbacksWrapper>
        </MenuWrapper>
        <Group id='main-layout' style={{ minHeight: '0' }}>
          <Panel id='main-content' defaultSize={'100%'}>
            <GenericFallbacksWrapper>
              <Container>
                {!noCpNav && showHeader && (
                  <HeaderContainer>
                    <Header>{innerHeaderMiddleSlot}</Header>
                  </HeaderContainer>
                )}

                <ChildrenContainer>
                  <Outlet />
                </ChildrenContainer>
              </Container>
            </GenericFallbacksWrapper>
          </Panel>
          <ChatBotSection />
        </Group>
      </div>
      <AddDealFormModal />
      {PermissionService.get().hasPermission(PermissionKey.canEditDeal) && <DealActionModals />}
    </>
  );
}

function getHeaderChildrenByLocation(location: string) {
  const isKPITemplatesLocation = location.includes(`${ROUTES.KPI}/${ROUTES.KPI_TEMPLATES}`);

  if (isKPITemplatesLocation) {
    return null;
  }

  // Entity Management puts its own actions on the header row next to the breadcrumb,
  // in place of the global search, matching the module's design. Compared by segment
  // so a future '/entities-something' route is not swept up by a prefix match.
  if (location.split('/')[1] === ROUTES.ENTITIES) {
    return <EntityManagementHeaderActions />;
  }

  return <Header2 />;
}

function getInnerNavComponent(location: string) {
  const urlSegments = location.split('/');
  const loc = urlSegments[1];

  if (PermissionService.get().hasPersona(PersonaKey.managedServices)) {
    return null;
  }

  switch (loc) {
    case ROUTES.PORTFOLIO:
      return <PortfolioReportingNavigation />;
    case ROUTES.DEAL_FLOW:
      return <DealflowNavigation />;
    case ROUTES.ADMIN_PANEL:
      return <AdminPanelNavigation />;
    case ROUTES.KPI:
      return <KPINavigation />;
    case ROUTES.COMPANIES:
      return <CPNavigation />;
    case ROUTES.FINANCE2:
      return <FinanceNavigation />;
    case ROUTES.ENTITIES:
      return <EntityManagementNavigation />;
    case ROUTES.SOURCING:
      return location.includes(`/${ROUTES.SOURCING}/${ROUTES.SOURCING_PROFILE}/`) ? null : (
        <SourcingNavigation />
      );
    default:
      return null;
  }
}
